import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { brl, dateFmt, nfmt } from "@/lib/format";
import { Download, ArrowLeft } from "lucide-react";

export default function QuoteDetail() {
  const { id } = useParams();
  const [q, setQ] = useState(null);

  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => setQ(r.data));
  }, [id]);

  const downloadPdf = async () => {
    const res = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${q.number || "orcamento"}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!q) return <div className="text-slate-400 text-sm">Carregando…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link
          to="/orcamentos"
          className="text-xs text-slate-400 hover:text-orange-300 flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> voltar
        </Link>
        <button
          onClick={downloadPdf}
          data-testid="quote-export-pdf-button"
          className="pill-accent rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-2"
        >
          <Download className="w-4 h-4" /> Baixar PDF
        </button>
      </div>

      <div className="card-tactical rounded-lg p-6">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="text-[11px] mono uppercase tracking-[0.3em] text-orange-400/80">
              orçamento
            </div>
            <h1 className="display text-4xl font-bold mono mt-1">{q.number}</h1>
            <div className="text-sm text-slate-400 mt-1">{dateFmt(q.created_at)}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-slate-400">Status</div>
            <span className="mt-1 inline-block text-sm px-3 py-1 rounded-full bg-orange-500/10 text-orange-300 border border-orange-500/40">
              {q.status}
            </span>
            <div className="mt-4 text-[11px] uppercase tracking-widest text-slate-400">
              Total
            </div>
            <div className="display text-3xl font-bold mono text-orange-300">
              {brl(q.totals?.total)}
            </div>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">
              Cliente
            </div>
            <div className="text-slate-100 font-medium">{q.client?.name || "—"}</div>
            <div className="text-sm text-slate-400 mt-1">
              {q.client?.doc_number}
              <div>{q.client?.email} · {q.client?.phone}</div>
              <div>
                {q.client?.city} {q.client?.state && `/ ${q.client.state}`}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Corte total" value={`${nfmt(q.totals?.total_cut_length_m)} m`} />
            <MiniStat label="Furos totais" value={q.totals?.total_holes} />
          </div>
        </div>
      </div>

      <div className="card-tactical rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-4 py-2.5">Peça</th>
                <th className="text-right px-4 py-2.5">Qtd</th>
                <th className="text-right px-4 py-2.5">Corte (m)</th>
                <th className="text-right px-4 py-2.5">Furos</th>
                <th className="text-right px-4 py-2.5">R$/m</th>
                <th className="text-right px-4 py-2.5">R$/furo</th>
                <th className="text-right px-4 py-2.5">Total</th>
              </tr>
            </thead>
            <tbody>
              {q.items.map((it, i) => (
                <tr key={i} className="border-t border-slate-800">
                  <td className="px-4 py-3">
                    {it.part_name}
                    {it.dxf_filename && (
                      <div className="text-[11px] mono text-slate-500">{it.dxf_filename}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right mono">{it.quantity}</td>
                  <td className="px-4 py-3 text-right mono">{nfmt(it.cut_length_m)}</td>
                  <td className="px-4 py-3 text-right mono">{it.holes_count}</td>
                  <td className="px-4 py-3 text-right mono">{brl(it.price_per_meter_cut)}</td>
                  <td className="px-4 py-3 text-right mono">{brl(it.price_per_hole)}</td>
                  <td className="px-4 py-3 text-right mono text-orange-300">
                    {brl(it.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-tactical rounded-lg p-6 max-w-md ml-auto">
        <SumRow label="Subtotal" value={brl(q.totals?.subtotal)} />
        <SumRow label={`Desconto (${q.discount_percent}%)`} value={"- " + brl(q.totals?.discount_value)} />
        <SumRow label="Setup" value={brl(q.setup_fee)} />
        <SumRow label={`Impostos (${q.tax_percent}%)`} value={brl(q.totals?.tax_value)} />
        <div className="border-t border-slate-800 mt-3 pt-3 flex justify-between items-baseline">
          <span className="text-sm uppercase tracking-widest text-orange-400">Total</span>
          <span className="display text-3xl font-bold mono text-orange-300">
            {brl(q.totals?.total)}
          </span>
        </div>
      </div>

      {q.notes && (
        <div className="card-tactical rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">
            Observações
          </div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap">{q.notes}</div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="display text-xl font-bold mono text-orange-300 mt-1">{value}</div>
    </div>
  );
}

function SumRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline text-sm py-1">
      <span className="text-slate-400">{label}</span>
      <span className="mono text-slate-200">{value}</span>
    </div>
  );
}
